import { useState } from 'react';
import { useI18n } from '../../lib/i18n';
import Modal from '../ui/Modal';
import { Button } from '../ui/Button';
import { inviteMember, type InvitableRole } from '../../lib/team';
import type { RoleDoc } from '../../lib/permissions';

export interface InviteMemberModalProps {
  open: boolean;
  onClose: () => void;
  roles: RoleDoc[];
  onInvited: () => Promise<void>;
}

export function InviteMemberModal({
  open,
  onClose,
  roles,
  onInvited,
}: InviteMemberModalProps) {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<InvitableRole>('agent');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleInvite() {
    const trimmed = email.trim();
    if (!trimmed) return;
    setBusy(true);
    setErr(null);
    try {
      await inviteMember({ email: trimmed, role });
      setEmail('');
      await onInvited();
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('team.inviteTitle')}
    >
      <div className="space-y-4 pt-1">
        <p className="text-xs text-ink-3 dark:text-slate-400">
          {t('team.inviteSubtitle')}
        </p>

        {err && (
          <div className="rounded-[9px] bg-danger-50 px-3 py-2 text-xs font-semibold text-danger dark:bg-rose-950/40 dark:text-rose-300">
            {err}
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
            {t('team.email')}
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="colleague@company.com"
            className="mt-1 w-full rounded-[9px] border border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
            {t('team.role')}
          </label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as InvitableRole)}
            className="mt-1 w-full rounded-[9px] border border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
          >
            {roles.filter((r) => r.id !== 'owner').map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-line dark:border-slate-800">
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            size="sm"
            disabled={busy || !email.trim()}
            onClick={handleInvite}
          >
            {busy ? t('team.inviting') : t('team.sendInvite')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default InviteMemberModal;
